import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

interface UserTeam {
  team_id: string;
  team_name: string;
  team_color: string;
  [key: string]: any; // For dynamic permissions
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  org_id: string;
  teams: UserTeam[];
  // Permissões agregadas (combinadas de todas as equipes)
  permissions: Record<string, boolean>;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  // Helper methods for permissions
  hasPermission: (permission: string) => boolean;
  // Legacy permissions (for backward compatibility)
  canManageContracts: boolean;
  canManageWorkflows: boolean;
  canApproveContracts: boolean;
  canViewAllContracts: boolean;
  canCreateContracts: boolean;
  canEditContracts: boolean;
  canDeleteContracts: boolean;
  canManageTemplates: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (userId: string) => {
    try {
      // Buscar usuário com suas equipes - usar * para pegar TODAS as colunas
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          is_active,
          is_admin,
          org_id,
          user_teams!user_teams_user_id_fkey(
            team_id,
            teams!inner(*)
          )
        `)
        .eq('id', userId)
        .single();

      if (userError) {
        logger.error('Error loading user profile', userError);
        setProfile(null);
        setLoading(false);
        return;
      }

      // Mapear equipes com TODAS as permissões
      const teams: UserTeam[] = (userData.user_teams || []).map((ut: any) => {
        const team: UserTeam = {
          team_id: ut.team_id,
          team_name: ut.teams.name,
          team_color: ut.teams.color,
        };

        // Copiar todas as permissões (colunas que começam com 'can_')
        Object.keys(ut.teams).forEach(key => {
          if (key.startsWith('can_')) {
            team[key] = ut.teams[key];
          }
        });

        return team;
      });

      // Agregar permissões de todas as equipes (OR lógico)
      const permissions: Record<string, boolean> = {};

      // Get all unique permission keys from all teams
      const allPermissionKeys = new Set<string>();
      teams.forEach(team => {
        Object.keys(team).forEach(key => {
          if (key.startsWith('can_')) {
            allPermissionKeys.add(key);
          }
        });
      });

      // Aggregate permissions using OR logic (user has permission if ANY team has it)
      allPermissionKeys.forEach(permKey => {
        permissions[permKey] = teams.some(t => t[permKey] === true);
      });

      setProfile({
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name,
        is_active: userData.is_active,
        is_admin: userData.is_admin,
        org_id: userData.org_id,
        teams,
        permissions,
      });
    } catch (error) {
      logger.error('Error loading profile', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Helper to check any permission
  const hasPermission = (permission: string): boolean => {
    if (profile?.is_admin) return true;
    return profile?.permissions[permission] || false;
  };

  // Permissões calculadas (admin sempre tem todas)
  const isAdmin = profile?.is_admin || false;
  const canViewAllContracts = hasPermission('can_view_all_contracts');
  const canCreateContracts = hasPermission('can_create_contracts');
  const canEditContracts = hasPermission('can_edit_contracts');
  const canDeleteContracts = hasPermission('can_delete_contracts');
  const canApproveContracts = hasPermission('can_approve_contracts');
  const canManageTemplates = hasPermission('can_manage_templates');
  const canManageWorkflows = hasPermission('can_manage_workflows');

  // Compatibilidade com código antigo
  const canManageContracts = isAdmin || canEditContracts || canDeleteContracts;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        isAdmin,
        hasPermission,
        canManageContracts,
        canViewAllContracts,
        canCreateContracts,
        canEditContracts,
        canDeleteContracts,
        canApproveContracts,
        canManageTemplates,
        canManageWorkflows,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
